import { useMemo, useState } from "react";
import {
    Box,
    Stack,
    Typography,
    IconButton,
    Alert,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import { createPayrollClosure, saveSelectedEmployees } from "../api/liquidacionApi";
import { mapApiError } from "../../../../shared/utils/mapApiError";
import { fmtYMD, fmtDMY, ymdToDate, addDaysYMD } from "../../../../shared/utils/dateFormat";
import { getCalendarGrid } from "../../../../shared/utils/calendarUtils";
function calcDefaultName() {
    return "";
}

function pillInputSx() {
    return {
        "& .MuiOutlinedInput-root": {
            borderRadius: "999px",
            bgcolor: "white",
        },
    };
}
function Pill({ children, sx }) {
    return (
        <Box
            sx={{
                px: 2.5,
                py: 1.1,
                bgcolor: "secondary.50",
                borderRadius: "33px",
                boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

function isSameYMD(a, b) {
    return Boolean(a) && Boolean(b) && a === b;
}

function isBetween(ymd, start, end) {
    if (!ymd || !start || !end) return false;
    return ymd >= start && ymd <= end;
}

function clampRange(start, end) {
    if (!start || !end) return { start, end };
    if (start <= end) return { start, end };
    return { start: end, end: start };
}

function CalendarRangeTwoMonths({ start, end, onChangeRange, maxSelectableYMD, leftMonthCursor, onLeftMonthCursorChange }) {
    const startDate = start ? ymdToDate(start) : new Date();

    const [internalLeftCursor, setInternalLeftCursor] = useState(
        new Date(startDate.getFullYear(), startDate.getMonth(), 1)
    );

    const leftCursor = leftMonthCursor || internalLeftCursor;
    const setLeftCursor = onLeftMonthCursorChange || setInternalLeftCursor;

    const rightCursor = useMemo(
        () => new Date(leftCursor.getFullYear(), leftCursor.getMonth() + 1, 1),
        [leftCursor]
    );

    const leftDays = useMemo(() => getCalendarGrid(leftCursor), [leftCursor]);
    const rightDays = useMemo(() => getCalendarGrid(rightCursor), [rightCursor]);

    const range = useMemo(() => clampRange(start, end), [start, end]);

    const isDisabledDay = (ymd) => {
        if (!maxSelectableYMD) return false;
        return ymd > maxSelectableYMD; // futuro deshabilitado
    };

    const pick = (ymd) => {
        if (isDisabledDay(ymd)) return;

        // Reglas:
        // - Para personalizado se deben seleccionar 2 días.
        // - Primer click: setea start
        // - Segundo click: setea end
        // - Si ya hay start+end: reinicia (nuevo start)
        if (!start || (start && end)) {
            onChangeRange(ymd, "");
            return;
        }

        // si hay start y no hay end
        const next = clampRange(start, ymd);
        onChangeRange(next.start, next.end);
    };

    const renderMonth = (cursor, days) => {
        const monthLabel = cursor.toLocaleDateString("es-AR", { month: "short" }).replace(".", "");
        const yearLabel = String(cursor.getFullYear());

        return (
            <Box sx={{ flex: 1, minWidth: 280 }}>
                <Stack direction="row" sx={{ alignItems: "center", justifyContent: "center", gap: 2, mb: 1 }}>
                    <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "primary.900", textTransform: "lowercase" }}>
                            {monthLabel}
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: "primary.700" }}>▼</Typography>
                    </Stack>
                    <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "primary.900" }}>{yearLabel}</Typography>
                        <Typography sx={{ fontSize: 14, color: "primary.700" }}>▼</Typography>
                    </Stack>
                </Stack>

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
                    {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
                        <Typography key={d} sx={{ fontSize: 11, color: "primary.600", textAlign: "center" }}>
                            {d}
                        </Typography>
                    ))}
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
                    {days.map((day) => {
                        const inMonth = day.type === "current";
                        const selectedStart = isSameYMD(day.ymd, range.start);
                        const selectedEnd = isSameYMD(day.ymd, range.end);
                        const inRange = isBetween(day.ymd, range.start, range.end);

                        const isAnchor = selectedStart || selectedEnd;
                        const disabled = isDisabledDay(day.ymd);

                        return (
                            <Box
                                key={day.ymd}
                                onClick={() => pick(day.ymd)}
                                sx={{
                                    height: 30,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: isAnchor ? "8px" : "6px",
                                    bgcolor: isAnchor
                                        ? "#1976d2"
                                        : disabled
                                            ? "rgba(0,0,0,0.06)"
                                            : inRange
                                                ? "rgba(25, 118, 210, 0.25)"
                                                : "transparent",
                                    color: isAnchor
                                        ? "white"
                                        : disabled
                                            ? "rgba(0,0,0,0.35)"
                                            : inMonth
                                                ? "primary.900"
                                                : "primary.400",
                                    fontSize: 13,
                                    fontWeight: isAnchor ? 700 : 500,
                                    cursor: disabled ? "not-allowed" : "pointer",
                                    userSelect: "none",
                                    opacity: inMonth ? 1 : 0.5,
                                    "&:hover": disabled
                                        ? {}
                                        : {
                                            bgcolor: isAnchor
                                                ? "#1565c0"
                                                : inRange
                                                    ? "rgba(25, 118, 210, 0.3)"
                                                    : "rgba(25, 118, 210, 0.12)",
                                        },
                                }}
                                title={disabled ? "No se pueden seleccionar días futuros" : ""}
                            >
                                {day.label}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                bgcolor: "white",
                borderRadius: "12px",
                p: 2,
                boxShadow: "0px 0px 0px 1px rgba(0,0,0,0.08)",
            }}
        >
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <IconButton
                    onClick={() => setLeftCursor((p) => new Date(p.getFullYear(), p.getMonth() - 1, 1))}
                    size="small"
                >
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "primary.700" }}>‹</Typography>
                </IconButton>

                <Box sx={{ flex: 1 }} />

                <IconButton
                    onClick={() => setLeftCursor((p) => new Date(p.getFullYear(), p.getMonth() + 1, 1))}
                    size="small"
                >
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "primary.700" }}>›</Typography>
                </IconButton>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} sx={{ gap: 3, alignItems: "flex-start" }}>
                {renderMonth(leftCursor, leftDays)}
                {renderMonth(rightCursor, rightDays)}
            </Stack>
        </Box>
    );
}

export default function CreateClosureWizardPage() {
    const navigate = useNavigate();
    const location = useLocation();

    const recoveryEmployeeIds = location.state?.recoveryEmployeeIds;
    const isRecoveryMode = location.state?.isRecoveryMode;

    // Hoy no se cuenta; máximo seleccionable = ayer
    const todayYMD = fmtYMD(new Date());
    const maxSelectableYMD = addDaysYMD(todayYMD, -1);

    const [name, setName] = useState(isRecoveryMode ? 'Cierre de Recupero' : calcDefaultName());

    // Preset por defecto: últimos 7 días SIN contar hoy => fin = ayer
    const [periodEnd, setPeriodEnd] = useState(maxSelectableYMD);
    const [periodStart, setPeriodStart] = useState(addDaysYMD(maxSelectableYMD, -7));

    // Cursor controlado del calendario (mes izquierdo)
    const [leftMonthCursor, setLeftMonthCursor] = useState(() => {
        const d = ymdToDate(periodStart) || new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });

    const [shortcut, setShortcut] = useState("last7");
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ open: false, type: "error", message: "" });

    const showAlert = (type, message) => {
        setAlert({ open: true, type, message });
        setTimeout(() => setAlert({ open: false, type: "error", message: "" }), 4000);
    };

    const moveCalendarToRange = (startYmd) => {
        const d = ymdToDate(startYmd);
        if (!d) return;
        setLeftMonthCursor(new Date(d.getFullYear(), d.getMonth(), 1));
    };

    const applyShortcut = (key) => {
        // Todos los presets: fin = ayer (no cuenta hoy)
        const end = maxSelectableYMD;

        if (key === "last7") {
            const start = addDaysYMD(end, -7);
            setPeriodEnd(end);
            setPeriodStart(start);
            moveCalendarToRange(start);
        }
        if (key === "last10") {
            const start = addDaysYMD(end, -10);
            setPeriodEnd(end);
            setPeriodStart(start);
            moveCalendarToRange(start);
        }
        if (key === "last30") {
            const start = addDaysYMD(end, -30);
            setPeriodEnd(end);
            setPeriodStart(start);
            moveCalendarToRange(start);
        }
        if (key === "lastMonth") {
            const today = new Date();
            const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);

            const start = fmtYMD(firstDay);
            const endLastMonth = fmtYMD(lastDay);

            setPeriodEnd(endLastMonth);
            setPeriodStart(start);
            moveCalendarToRange(start);
        }
    };

    const validateForm = () => {
        const ne = {};
        const trimmedName = name.trim();
        if (!trimmedName) {
            ne.name = "El nombre del cierre es requerido";
        } else if (trimmedName.length < 3) {
            ne.name = "El nombre debe tener al menos 3 caracteres";
        }

        if (!periodStart) ne.periodStart = "Seleccioná el día de inicio";
        if (!periodEnd) ne.periodEnd = "Seleccioná el día de fin";

        // Rango personalizado debe elegir 2 días
        if (periodStart && !periodEnd) ne.periodEnd = "Para un rango personalizado, seleccioná 2 días";

        if (periodStart && periodEnd) {
            const s = ymdToDate(periodStart);
            const e = ymdToDate(periodEnd);

            if (periodStart > maxSelectableYMD || periodEnd > maxSelectableYMD) {
                ne.periodEnd = "No se pueden seleccionar días futuros";
            }
            if (s > e) ne.periodEnd = "La fecha de fin debe ser posterior a la de inicio";
            if (s.toDateString() === e.toDateString()) ne.periodEnd = "Seleccioná dos días distintos";
        }

        setErrors(ne);
        return Object.keys(ne).length === 0;
    };

    const handleContinue = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);
            const res = await createPayrollClosure({
                name: name.trim(),
                periodStart,
                periodEnd,
            });
            const closure = res?.data || res;

            showAlert("success", "Cierre creado exitosamente");

            setTimeout(async () => {
                if (closure?.id) {
                    if (isRecoveryMode && recoveryEmployeeIds?.length > 0) {
                        // Si es recupero, inyectamos a los olvidados y saltamos al Paso 3
                        await saveSelectedEmployees(closure.id, recoveryEmployeeIds);
                        navigate(`/rrhh/liquidacion/${closure.id}/datos`);
                    } else {
                        navigate(`/rrhh/liquidacion/${closure.id}/usuarios`);
                    }
                } else {
                    navigate("/rrhh/liquidacion");
                }
            }, 250);
        } catch (err) {
            const e = mapApiError(err);
            showAlert("error", e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Header igual a pantalla anterior */}
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
                <IconButton
                    onClick={() => navigate("/rrhh/liquidacion")}
                    sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "secondary.500",
                        "&:hover": { bgcolor: "secondary.600" },
                    }}
                >
                    <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
                </IconButton>
                <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
                    <Pill sx={{ bgcolor: "secondary.200" }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>
                            Cierres Faltantes
                        </Typography>
                    </Pill>
                </Stack>
            </Stack>

            {alert.open && (
                <Alert severity={alert.type} onClose={() => setAlert({ ...alert, open: false })} sx={{ mt: 2 }}>
                    {alert.message}
                </Alert>
            )}

            {/* Contenedor full width */}
            <Box sx={{ width: "100%", mt: 2 }}>
                {/* Nombre */}
                <Stack direction={{ xs: "column", md: "row" }} sx={{ gap: 2, alignItems: "center", mb: 3 }}>
                    <Box
                        sx={{
                            px: 2.5,
                            py: 1.2,
                            borderRadius: "999px",
                            border: "2px solid rgba(0,0,0,0.15)",
                            bgcolor: "white",
                            minWidth: 180,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
                            Nombre de cierre:
                        </Typography>
                    </Box>

                    <TextField
                        fullWidth
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (errors.name) setErrors((p) => ({ ...p, name: "" }));
                        }}
                        error={!!errors.name}
                        helperText={errors.name}
                        size="small"
                        placeholder=""
                        sx={pillInputSx()}
                    />
                </Stack>

                {/* Cuerpo */}
                <Stack direction={{ xs: "column", md: "row" }} sx={{ gap: 3, alignItems: "flex-start" }}>
                    {/* Izq */}
                    <Box sx={{ width: { xs: "100%", md: 280 } }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: "primary.900", mb: 2 }}>
                            Seleccionar fecha
                        </Typography>

                        <RadioGroup
                            value={shortcut}
                            onChange={(e) => {
                                const v = e.target.value;
                                setShortcut(v);
                                applyShortcut(v);
                            }}
                            sx={{ gap: 1.5 }}
                        >
                            <FormControlLabel
                                value="last7"
                                control={<Radio />}
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            color: "#1976d2",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        Ultimos 7 días
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                value="last10"
                                control={<Radio />}
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            color: "#1976d2",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        Ultimas 10 días
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                value="last30"
                                control={<Radio />}
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            color: "#1976d2",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        Ultimos 30 días
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                value="lastMonth"
                                control={<Radio />}
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            color: "#1976d2",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        Ultimo mes
                                    </Typography>
                                }
                            />
                        </RadioGroup>
                    </Box>

                    {/* Separador: en mock casi no se ve; dejar una línea muy sutil solo en desktop */}
                    <Box
                        sx={{
                            display: { xs: "none", md: "block" },
                            width: "1px",
                            bgcolor: "rgba(0,0,0,0.08)",
                            alignSelf: "stretch",
                        }}
                    />

                    {/* Der */}
                    <Box sx={{ flex: 1, width: "100%" }}>
                        <CalendarRangeTwoMonths
                            start={periodStart}
                            end={periodEnd}
                            maxSelectableYMD={maxSelectableYMD}
                            leftMonthCursor={leftMonthCursor}
                            onLeftMonthCursorChange={setLeftMonthCursor}
                            onChangeRange={(s, e) => {
                                setPeriodStart(s);
                                setPeriodEnd(e);
                                if (s) moveCalendarToRange(s);
                                setShortcut("custom");
                                setErrors((p) => ({ ...p, periodStart: "", periodEnd: "" }));
                            }}
                        />

                        {(errors.periodStart || errors.periodEnd) && (
                            <Typography sx={{ mt: 1.5, fontSize: 12, color: "error.main", fontWeight: 600 }}>
                                {errors.periodStart || errors.periodEnd}
                            </Typography>
                        )}

                        <Typography sx={{ mt: 1, fontSize: 12, color: "primary.700" }}>
                            Rango seleccionado: {periodStart ? fmtDMY(ymdToDate(periodStart)) : "—"} → {periodEnd ? fmtDMY(ymdToDate(periodEnd)) : "—"}
                        </Typography>

                        <Stack direction="row" sx={{ justifyContent: "flex-end", mt: 6 }}>
                            <Button
                                onClick={handleContinue}
                                disabled={loading || name.trim().length < 3}
                                sx={{
                                    bgcolor: "#b9f6ca",
                                    color: "primary.900",
                                    px: 2.5,
                                    py: 1,
                                    minWidth: 48,
                                    height: 48,
                                    borderRadius: "24px",
                                    border: "2px solid",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    fontSize: 16,
                                    "&:hover": { bgcolor: "#a5f5bd" },
                                    "&:disabled": { opacity: 0.6 },
                                }}
                            >
                                {loading ? "Continuando..." : "Continuar"}
                            </Button>
                        </Stack>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
}
